/**
 * Clone blank template rows into a sheet layout so expandable custom tables
 * visually match Apps Script insertRows behavior.
 */

import {
  getExpandableVisibleRowCount,
  resolveCustomSheetLayouts
} from './customSheetFields';

const ROW_MATRIX_KEYS = [
  'values',
  'displayValues',
  'formulas',
  'backgrounds',
  'fontColors',
  'fontFamilies',
  'fontSizes',
  'fontWeights',
  'fontStyles',
  'horizontalAlignments',
  'verticalAlignments',
  'numberFormats',
  'wrapStrategies',
  'textDirections',
  'checkboxes',
  'cellKinds'
];

const cloneRow = (matrix, row0) => {
  if (!Array.isArray(matrix) || !matrix[row0]) return null;
  const row = matrix[row0];
  return Array.isArray(row) ? row.map(cell => cell) : row;
};

const insertRowsIntoMatrix = (matrix, atRow0, count, templateRow0) => {
  if (!Array.isArray(matrix)) return matrix;
  const next = matrix.map(row => (Array.isArray(row) ? [...row] : row));
  const template = cloneRow(next, templateRow0) || (next[0] ? next[0].map(() => '') : []);
  const inserts = Array.from({ length: count }, () =>
    Array.isArray(template) ? template.map(cell => cell) : template
  );
  next.splice(atRow0, 0, ...inserts);
  return next;
};

const shiftSparseBorders = (borders, atRow1, count) => {
  if (!borders || typeof borders !== 'object' || borders.omitted) return borders;
  const next = {};
  Object.keys(borders).forEach(key => {
    const [r, c] = key.split(',').map(Number);
    if (!Number.isFinite(r) || !Number.isFinite(c)) {
      next[key] = borders[key];
      return;
    }
    const newR = r >= atRow1 ? r + count : r;
    next[`${newR},${c}`] = borders[key];
  });
  return next;
};

const shiftMerges = (merges, atRow1, count) =>
  (merges || []).map(merge => {
    const startRow = merge.startRow;
    const endRow = merge.endRow;
    if (endRow < atRow1) return { ...merge };
    if (startRow >= atRow1) {
      return {
        ...merge,
        startRow: startRow + count,
        endRow: endRow + count
      };
    }
    // Merge spans the insert point — extend end
    return {
      ...merge,
      endRow: endRow + count
    };
  });

const cloneTemplateRowMerges = (merges, templateRow1, count) => {
  const templateMerges = (merges || []).filter(
    merge => merge.startRow === templateRow1 && merge.endRow === templateRow1
  );

  return Array.from({ length: count }, (_, index) =>
    templateMerges.map(merge => ({
      ...merge,
      startRow: templateRow1 + index + 1,
      endRow: templateRow1 + index + 1
    }))
  ).flat();
};

const shiftVariableCells = (variableCells, atRow1, count) =>
  (variableCells || []).map(cell => {
    if (!cell || cell.row < atRow1) return cell;
    return { ...cell, row: cell.row + count };
  });

const shiftHiddenRows = (hiddenRows, atRow1, count) =>
  (hiddenRows || []).map(row => (row >= atRow1 ? row + count : row));

/**
 * Insert `count` clones of the template row after `atRow1` (1-indexed),
 * matching Apps Script insertRowsAfter(atRow1, count).
 */
export const insertClonedSheetRows = (sheet, atRow1, count) => {
  if (!sheet || count <= 0) return sheet;

  const templateRow0 = atRow1 - 1;
  const insertAt0 = atRow1; // 0-based index of the first row after the template
  const next = { ...sheet, rows: (sheet.rows || 0) + count };

  ROW_MATRIX_KEYS.forEach(key => {
    if (sheet[key]) {
      next[key] = insertRowsIntoMatrix(sheet[key], insertAt0, count, templateRow0);
    }
  });

  if (Array.isArray(sheet.rowHeights)) {
    const heights = [...sheet.rowHeights];
    const templateH = heights[templateRow0] ?? 21;
    heights.splice(insertAt0, 0, ...Array.from({ length: count }, () => templateH));
    next.rowHeights = heights;
  }

  // Rows below the template shift down.
  const shiftFromRow1 = atRow1 + 1;
  next.merges = [
    ...shiftMerges(sheet.merges, shiftFromRow1, count),
    ...cloneTemplateRowMerges(sheet.merges, atRow1, count)
  ];
  next.variableCells = shiftVariableCells(sheet.variableCells, shiftFromRow1, count);
  next.hiddenRows = shiftHiddenRows(sheet.hiddenRows, shiftFromRow1, count);
  next.borders = shiftSparseBorders(sheet.borders, shiftFromRow1, count);

  return next;
};

/** Expand a sheet layout for all expandable custom tables bound to documentState. */
export const expandSheetForCustomTables = (sheet, documentState) => {
  if (!sheet?.name) return sheet;

  const plan = getExpandableRowInsertPlan(sheet.name, documentState);
  if (!plan.length) return sheet;

  return plan.reduce(
    (working, { startRow, extra }) => insertClonedSheetRows(working, startRow, extra),
    sheet
  );
};

/**
 * Bottom-to-top insert plan matching expandSheetForCustomTables.
 * Used by the formula engine so HyperFormula addRows stays aligned with the visual sheet.
 * @returns {{ startRow: number, extra: number, field: string }[]}
 */
export const getExpandableRowInsertPlan = (sheetName, documentState) => {
  const expandableLayouts = resolveCustomSheetLayouts(sheetName).filter(l => l.expandable);
  if (!expandableLayouts.length) return [];

  return [...expandableLayouts]
    .sort((a, b) => b.startRow - a.startRow)
    .map(layout => {
      const visible = getExpandableVisibleRowCount(documentState, layout);
      const extra = Math.max(0, visible - 1);
      return { startRow: layout.startRow, extra, field: layout.field };
    })
    .filter(step => step.extra > 0);
};
